import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Video, Upload as UploadIcon, Trash2, Eye, Download, Loader2, AlertCircle } from "lucide-react";
import {
  drListFiles, drTeamDownloadUrl, drTeamThumbUrl, drTeamInlineUrl, drDeleteFile,
} from "../lib/api";
import { fmtDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import ChunkedUploader from "../components/dataroom/ChunkedUploader";
import MediaLightbox from "../components/dataroom/MediaLightbox";

const KATEGORIE = "13_Bilder_Videos";

const fmtSize = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const isVideoLike = (f) =>
  f.media_type === "video" ||
  (f.mime_type || "").startsWith("video/") ||
  /\.(mp4|mov|m4v|avi|mkv|webm)$/i.test(f.filename_original || "");

const isImageLike = (f) =>
  f.media_type === "image" ||
  (f.mime_type || "").startsWith("image/") ||
  /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(f.filename_original || "");

export default function BilderVideos() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await drListFiles({ kategorie: KATEGORIE });
      // Sort by hochgeladen_am desc
      const items = (data.items || []).sort(
        (a, b) => (b.hochgeladen_am || "").localeCompare(a.hochgeladen_am || "")
      );
      setFiles(items);
    } catch (e) {
      toast.error("Bilder/Videos konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-Poll alle 4 s, wenn mind. 1 Datei noch in „processing" oder ohne Thumb steckt.
  // Sobald alle finalisiert sind, Polling stoppen — kein dauernder Backend-Hit.
  const pollRef = useRef(null);
  useEffect(() => {
    const hasPending = files.some((f) => f.processing === true || (f.thumb_storage_path == null && !f.processing_error));
    if (hasPending) {
      pollRef.current = setInterval(() => load(), 4000);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }
  }, [files, load]);

  const lightboxItems = useMemo(
    () =>
      files
        .filter((f) => isImageLike(f) || isVideoLike(f))
        .map((f) => ({
          id: f.id,
          filename_original: f.filename_original,
          mime_type: f.mime_type,
          media_type: f.media_type || (isVideoLike(f) ? "video" : "image"),
          inlineUrl: drTeamInlineUrl(f.id),
          downloadUrl: drTeamDownloadUrl(f.id),
          kategorie_label: "Bilder & Videos",
        })),
    [files]
  );

  const onDelete = async (file) => {
    if (!window.confirm(`„${file.filename_original}" wirklich löschen?`)) return;
    try {
      await drDeleteFile(file.id);
      toast.success("Datei gelöscht");
      load();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-6 py-2">
      <PageHeader
        eyebrow="Newton Campus · Marketing-Asset"
        title="Bilder & Videos"
        subtitle="Sofort sichtbar für Investoren — kein NDA nötig. Drohnenvideos, Drohnen-Fotos, Innenansichten."
        testid="bv-header"
      />

      {/* Upload-Box */}
      <section className="border border-navy/12 bg-white p-5" data-testid="bv-upload-section">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="pb-eyebrow">Upload</div>
            <h2 className="font-display text-lg text-navy mt-1">Neue Aufnahmen hinzufügen</h2>
            <p className="text-xs font-sans text-navy/55 mt-1">
              iPhone-Safari ist unterstützt. HEIC wird automatisch nach JPG konvertiert. Drohnenvideos werden gestreamt.
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs font-sans text-navy/55 uppercase tracking-wider">Sichtbar</div>
            <div className="font-display text-sm text-navy mt-0.5">Sofort (vor NDA)</div>
          </div>
        </div>
        <ChunkedUploader
          kategorie={KATEGORIE}
          accept="image/*,video/*"
          onUploaded={() => load()}
          testidPrefix="bv-uploader"
        />
      </section>

      {/* Grid */}
      <section className="border border-navy/12 bg-white p-5" data-testid="bv-grid-section">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="pb-eyebrow">Galerie</div>
            <h2 className="font-display text-lg text-navy mt-1">
              {files.length} {files.length === 1 ? "Datei" : "Dateien"}
            </h2>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-navy/55 py-8 text-center font-sans">Lade…</div>
        ) : files.length === 0 ? (
          <div className="text-sm text-navy/55 py-12 text-center font-sans border border-dashed border-navy/15">
            Noch keine Bilder oder Videos hochgeladen.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="bv-grid">
            {files.map((f, idx) => {
              const isVid = isVideoLike(f);
              const lightboxIndex = lightboxItems.findIndex((it) => it.id === f.id);
              return (
                <article
                  key={f.id}
                  data-testid="bv-item"
                  className="group border border-navy/10 bg-ledger overflow-hidden flex flex-col"
                >
                  <div
                    className="relative aspect-[4/3] bg-navy/5 cursor-pointer overflow-hidden"
                    onClick={() => lightboxIndex >= 0 && setLightboxIdx(lightboxIndex)}
                  >
                    {(f.thumb_storage_path || f.media_type) ? (
                      <img
                        src={drTeamThumbUrl(f.id)}
                        alt={f.filename_original}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {isVid ? <Video size={28} className="text-navy/30" /> : <ImageIcon size={28} className="text-navy/30" />}
                      </div>
                    )}
                    {isVid && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-12 h-12 rounded-full bg-black/60 flex items-center justify-center">
                          <Video size={20} className="text-white ml-0.5" />
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/30 transition-colors pointer-events-none flex items-end justify-end p-2">
                      <Eye size={14} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="p-2.5 flex-1 flex flex-col gap-1">
                    <div className="text-xs text-navy truncate font-sans" title={f.filename_original}>
                      {f.filename_original}
                    </div>
                    <div className="text-[10px] text-navy/55 font-sans flex items-center justify-between">
                      <span>{fmtSize(f.size_bytes)}</span>
                      <span>{fmtDate(f.hochgeladen_am)}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <a
                        href={drTeamDownloadUrl(f.id)}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 text-[11px] border border-navy/15 text-navy hover:bg-navy hover:text-white transition-colors font-sans"
                        data-testid="bv-item-download"
                      >
                        <Download size={11} /> Download
                      </a>
                      <button
                        onClick={() => onDelete(f)}
                        title="Löschen"
                        className="p-1 text-navy/40 hover:text-tot transition-colors"
                        data-testid="bv-item-delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <MediaLightbox
        open={lightboxIdx >= 0}
        items={lightboxItems}
        index={lightboxIdx}
        onClose={() => setLightboxIdx(-1)}
        onPrev={() => setLightboxIdx((i) => (i - 1 + lightboxItems.length) % lightboxItems.length)}
        onNext={() => setLightboxIdx((i) => (i + 1) % lightboxItems.length)}
      />
    </div>
  );
}
